import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import dashboardService from '@api/v1/dashboardService'
import useReadState from '@hooks/useReadState'
import LoadingSpinner from '@components/common/LoadingSpinner'
import DashboardView from './DashboardView'

/**
 * The Dashboards experience: lands on the user's DEFAULT dashboard and offers a
 * switcher to change which one is shown (like legacy Centerbase — no catalog of
 * cards). The active dashboard is URL-driven (?d=<id>); with none, the default
 * (or first) is shown. Empty catalog → fall back to reports (spec 038).
 */
const DashboardsPanel = ({ selectedId, onSelect, onBrowseReports }) => {
  const listQuery = useQuery({
    queryKey: ['dashboards'],
    queryFn: () => dashboardService.list(),
    staleTime: 60_000,
    retry: false,
  })
  const { data, error, refetch } = listQuery

  /*
   * CC-53132 — three outcomes, not two. See `@api/v1/readState`.
   *
   * This used to branch on `isLoading`, which react-query defines as
   * `isPending && isFetching` — so it is FALSE for a query that is pending but
   * never sent. react-query PAUSES a query while `onlineManager` believes the
   * browser is offline: `queryFn` is never called, nothing throws, nothing logs,
   * `data` stays undefined, and the `= []` destructuring default then made this
   * panel say "You don't have any dashboards yet." and offer "Browse reports
   * instead" — routing the user away from dashboards they do have, on the
   * strength of a read that never happened. `retry: false` means it never
   * self-corrects either.
   */
  const listState = useReadState('dashboards', listQuery)

  // A shape fallback, no longer an ANSWER: every branch below that speaks about
  // this list is gated on the read having completed.
  const dashboards = useMemo(() => (Array.isArray(data) ? data : []), [data])

  const sorted = useMemo(
    () => [...dashboards].sort(
      (a, b) => (b.isDefault - a.isDefault) || (b.isPublic - a.isPublic) || String(a.title).localeCompare(String(b.title))
    ),
    [dashboards]
  )
  const defaultId = useMemo(() => {
    const def = dashboards.find((d) => d.isDefault) || dashboards[0]
    return def ? String(def.id) : ''
  }, [dashboards])
  const active = selectedId || defaultId

  // `idle` rides with `loading`. It means pending-but-never-started
  // (`enabled: false`), which this query cannot reach today — it has no `enabled`
  // option — but every branch here is an early RETURN, so an unhandled state
  // would fall past all of them and render the switcher around an empty
  // <select>. Cheaper to cover than to leave the trap for whoever adds a gate.
  if (listState === 'loading' || listState === 'idle') {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 flex justify-center">
        <LoadingSpinner message="Loading dashboards…" />
      </div>
    )
  }
  // The state that used to render as "your firm has no dashboards". No request
  // was sent, so there is nothing in any log to find — say so on screen instead,
  // and deliberately WITHOUT the "Browse reports instead" escape hatch: that
  // button acts on spec 038's "the firm really has no dashboards" premise, and
  // offering it here would route the user away from data they do have.
  if (listState === 'blocked') {
    return (
      <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
        <p className="font-medium">Can’t reach the server, so we haven’t loaded your dashboards.</p>
        <p className="mt-1 text-amber-800">
          This is not an empty list — your connection looks offline. Check it and try again.
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
        >
          Try again
        </button>
      </div>
    )
  }
  if (listState === 'failed') {
    return (
      <div role="alert" className="bg-red-50 border border-red-100 rounded-xl p-6 text-sm text-red-700">
        <p className="font-medium">Failed to load dashboards: {error?.message}</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-3 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
        >
          Try again
        </button>
      </div>
    )
  }
  // Only now is an empty list a fact about the firm rather than about the read.
  // The `ready` gate is written out even though the four branches above already
  // return, so the claim is verifiable at the site that makes it.
  if (listState === 'ready' && sorted.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-sm text-gray-500 mb-3">You don’t have any dashboards yet.</p>
        {onBrowseReports && (
          <button type="button" onClick={onBrowseReports} className="btn-cb-tertiary">
            Browse reports instead →
          </button>
        )}
      </div>
    )
  }

  return (
    <div>
      {/* Dashboard switcher — centered; lands on the default, lets the user change it. */}
      <div className="flex items-center justify-center gap-2 mb-5">
        <label htmlFor="dashboard-switcher" className="text-sm text-gray-500">Dashboard</label>
        <select
          id="dashboard-switcher"
          value={active}
          onChange={(e) => onSelect(e.target.value)}
          className="w-full min-w-[20rem] max-w-lg px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-cb-navy focus:outline-none focus:ring-2 focus:ring-cb-accent/30 focus:border-cb-accent"
        >
          {sorted.map((d) => (
            <option key={d.id} value={String(d.id)}>
              {d.title || 'Untitled'}{d.isDefault ? ' (default)' : ''}
            </option>
          ))}
        </select>
      </div>

      {active && <DashboardView id={active} />}
    </div>
  )
}

export default DashboardsPanel
